import React from "react";
import { useAuth } from "react-oidc-context";
import { Card, Typography, Button, Space, Divider } from "antd";

const { Title, Text, Paragraph } = Typography;

const AuthDebug: React.FC = () => {
  const auth = useAuth();

  const testApiCall = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/s3/upload?key=test.jpg`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user?.access_token || "NO_TOKEN"}`,
            "api-key": process.env.REACT_APP_API_KEY || "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ test: true }),
        },
      );

      console.log("Test API Response:", {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Test API Data:", data);
      } else {
        const errorText = await response.text();
        console.error("Test API Error:", errorText);
      }
    } catch (error) {
      console.error("Test API Call Failed:", error);
    }
  };

  const copyToken = () => {
    if (auth.user?.access_token) {
      navigator.clipboard.writeText(auth.user.access_token);
      alert("Token copied to clipboard!");
    } else {
      alert("No token available to copy");
    }
  };

  return (
    <Card title="Authentication Debug" style={{ margin: "20px" }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <div>
          <Title level={4}>Auth State</Title>
          <Text>Is Loading: {auth.isLoading ? "Yes" : "No"}</Text>
          <br />
          <Text>Is Authenticated: {auth.isAuthenticated ? "Yes" : "No"}</Text>
          <br />
          <Text>Has User: {auth.user ? "Yes" : "No"}</Text>
          <br />
          <Text>Has Error: {auth.error ? "Yes" : "No"}</Text>
        </div>

        <Divider />

        {auth.user && (
          <div>
            <Title level={4}>User Information</Title>
            <Text>Email: {(auth.user as any)?.email || "N/A"}</Text>
            <br />
            <Text>Name: {(auth.user as any)?.name || "N/A"}</Text>
            <br />
            <Text>Sub: {(auth.user as any)?.sub || "N/A"}</Text>
            <br />
            <Text>Token Type: {(auth.user as any)?.token_type || "N/A"}</Text>
            <br />
            <Text>
              Expires At:{" "}
              {(auth.user as any)?.expires_at
                ? new Date(
                    (auth.user as any).expires_at * 1000,
                  ).toLocaleString()
                : "N/A"}
            </Text>
          </div>
        )}

        <Divider />

        <div>
          <Title level={4}>Token Information</Title>
          <Text>
            Access Token Present: {auth.user?.access_token ? "Yes" : "No"}
          </Text>
          <br />
          <Text>
            ID Token Present: {(auth.user as any)?.id_token ? "Yes" : "No"}
          </Text>
          <br />
          <Text>
            Refresh Token Present:{" "}
            {(auth.user as any)?.refresh_token ? "Yes" : "No"}
          </Text>
        </div>

        {auth.user?.access_token && (
          <div>
            <Title level={5}>Access Token (First 50 chars)</Title>
            <Paragraph
              copyable
              style={{ fontFamily: "monospace", fontSize: "12px" }}
            >
              {auth.user.access_token.substring(0, 50)}...
            </Paragraph>
            <Button onClick={copyToken} size="small">
              Copy Full Token
            </Button>
          </div>
        )}

        <Divider />

        <div>
          <Title level={4}>LocalStorage Tokens</Title>
          <Text>
            ID Token: {localStorage.getItem("cognito_id_token") ? "Yes" : "No"}
          </Text>
          <br />
          <Text>
            Access Token:{" "}
            {localStorage.getItem("cognito_access_token") ? "Yes" : "No"}
          </Text>
          <br />
          <Text>
            Refresh Token:{" "}
            {localStorage.getItem("cognito_refresh_token") ? "Yes" : "No"}
          </Text>
        </div>

        <Divider />

        <div>
          <Title level={4}>Actions</Title>
          <Space>
            <Button onClick={testApiCall} type="primary">
              Test API Call
            </Button>
            <Button onClick={() => auth.signinRedirect()}>Sign In</Button>
            <Button onClick={() => auth.signoutRedirect()}>Sign Out</Button>
          </Space>
        </div>

        {auth.error && (
          <div>
            <Divider />
            <Title level={4}>Error Information</Title>
            <Text type="danger">{auth.error.message}</Text>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default AuthDebug;
